import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function DashboardCard04({ stockSymbol }) {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
    currency: 'USD' // Add currency to chartData
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!stockSymbol) {
        console.error('No stockSymbol provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`https://investorverden.onrender.com/api/financials?q=${stockSymbol}`);
        if (response.data) {
          const financialData = response.data.earnings.financialsChart.yearly.map(item => ({
            year: item.date,
            revenue: parseFloat(item.revenue),
            earnings: parseFloat(item.earnings)
          })).filter(item => parseInt(item.year) >= 2019);

          // Get currency from response
          const detectedCurrency = response.data.earnings?.financialCurrency || 'USD';
          
          setChartData({
            labels: financialData.map(data => data.year),
            datasets: [
              {
                label: 'Omsætning',
                data: financialData.map(data => data.revenue),
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                hoverBackgroundColor: 'rgba(75, 192, 192, 0.7)',
                barPercentage: 0.5,
                categoryPercentage: 0.8
              },
              {
                label: 'Indtjening',
                data: financialData.map(data => data.earnings),
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                hoverBackgroundColor: 'rgba(255, 99, 132, 0.7)',
                barPercentage: 0.5,
                categoryPercentage: 0.8
              }
            ],
            currency: detectedCurrency // Store currency in chartData
          });
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching financial data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [stockSymbol]);

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,  // This controls the height relative to width
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        display: false
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: { 
          label: function(context) {
            const value = context.parsed.y;
            let scaledValue = Math.abs(value);
            let suffix = '';
          
            if (scaledValue >= 1e12) {
              scaledValue /= 1e12;
              suffix = 'T';
            } else if (scaledValue >= 1e9) {
              scaledValue /= 1e9;
              suffix = 'B';
            } else if (scaledValue >= 1e6) {
              scaledValue /= 1e6;
              suffix = 'M';
            }
          
            const formattedValue = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(scaledValue);
          
            return `${context.dataset.label}: ${value < 0 ? '-' : ''}${chartData.currency} ${formattedValue}${suffix}`;
          }
        }
      },
      legend: {
        display: false,
        position: 'top'
      },
      title: {
        display: true,
        text: `Årlig omsætning og indtjening sammenligning (${chartData.currency})`
      }
    }
  };

  if (loading) {
    return <div>Loader...</div>;
  }

  return (
    <div className="flex flex-col col-span-full sm:col-span-6 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-4" style={{ minHeight: '400px' }}>
      <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100">
          Omsætning/Indtjening for {stockSymbol} ({chartData.currency})
        </h2>
      </header>
      <div style={{ height: '300px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

export default DashboardCard04;